import { Badge } from '@mdvip/shared/components/ui/badge';
import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddPaymentAccountModal } from '@/components/payments/accounts/AddPaymentAccountModal';
import { BankAccountListSection } from '@/components/payments/accounts/BankAccountListSection';
import { CreditCardListSection } from '@/components/payments/accounts/CreditCardListSection';
import { EditBankAccountModal } from '@/components/payments/accounts/EditBankAccountModal';
import { EditCreditCardModal } from '@/components/payments/accounts/EditCreditCardModal';
import { RemoveBankAccountDialog } from '@/components/payments/accounts/RemoveBankAccountDialog';
import { RemoveCardDialog } from '@/components/payments/accounts/RemoveCardDialog';
import { useSavedBankAccounts } from '@/hooks/payments/bankAccount/useSavedBankAccounts';
import { useSavedCreditCards } from '@/hooks/payments/creditCard/useSavedCreditCards';
import { SavedPaymentMethodsError, selectPaymentProfileIdsWithUpcomingPayment } from '@/hooks/payments/paymentApi';
import { useUpcomingPayments } from '@/hooks/payments/upcomingPayments/useUpcomingPayments';
import { useSetDefaultPaymentMethod } from '@/hooks/payments/useSetDefaultPaymentMethod';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import type { PaymentTab } from '@/lib/payments/paymentAccount.types';

export function PaymentAccountsPage() {
  const { t } = useTranslation('members');
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const [modalState, setModalState] = useState<{ open: boolean; tab: PaymentTab }>({
    open: false,
    tab: 'credit-card',
  });
  const [editingCard, setEditingCard] = useState<CreditCardAccount | null>(null);
  const [cardToRemove, setCardToRemove] = useState<CreditCardAccount | null>(null);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccountAccount | null>(null);
  const [accountToRemove, setAccountToRemove] = useState<BankAccountAccount | null>(null);
  const {
    data: cards = [],
    isLoading: isLoadingCards,
    isError: isCardError,
    isFetching: isFetchingCards,
    error: cardError,
  } = useSavedCreditCards();
  const { data: bankAccounts = [], isLoading: isLoadingBanks } = useSavedBankAccounts();
  const setDefault = useSetDefaultPaymentMethod();
  const { data: upcomingPayments = [] } = useUpcomingPayments();
  // Fails open: while loading or on error, no id is excluded, so Remove stays visible rather than
  // disappearing on a guard we can't currently confirm.
  const paymentProfileIdsWithUpcomingPayment = useMemo(
    () => selectPaymentProfileIdsWithUpcomingPayment(upcomingPayments),
    [upcomingPayments],
  );

  // Both hooks share one underlying query, so a fetch failure surfaces identically on either —
  // one toast covers it rather than firing twice.
  useEffect(() => {
    if (!isCardError || isFetchingCards) return;
    const code = cardError instanceof SavedPaymentMethodsError ? cardError.code : extractGqlErrorCode(cardError);
    toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
  }, [isCardError, isFetchingCards, cardError, toast, tError]);

  const openModal = (tab: PaymentTab) => setModalState({ open: true, tab });

  const handleSuccess = () => {
    toast({
      title: t('payments.addAccount.successToast.title'),
      description: t('payments.addAccount.successToast.description'),
      variant: 'success',
    });
    setModalState((state) => ({ ...state, open: false }));
  };

  const handleEditSuccess = () => {
    toast({
      title: t('payments.editAccount.successToast.title'),
      description: t('payments.editAccount.successToast.description'),
      variant: 'success',
    });
    setEditingCard(null);
  };

  const handleEditBankAccountSuccess = () => {
    toast({
      title: t('payments.editAccount.successToast.title'),
      description: t('payments.editAccount.successToast.description'),
      variant: 'success',
    });
    setEditingBankAccount(null);
  };

  const handleRemoveSuccess = () => {
    toast({ title: t('payments.pages.paymentAccounts.creditCards.removedToast'), variant: 'success' });
    setCardToRemove(null);
  };

  const handleRemoveBankAccountSuccess = () => {
    toast({ title: t('payments.pages.paymentAccounts.bankAccounts.removedToast'), variant: 'success' });
    setAccountToRemove(null);
  };

  const totalAccounts = cards.length + bankAccounts.length;

  return (
    <section>
      <div className="mb-6 flex items-start">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-h2 font-semibold text-foreground">
              {t('payments.pages.paymentAccounts.sectionTitle')}
            </h2>
            <Badge variant="secondary">
              {t('payments.pages.paymentAccounts.accountsBadge', { count: totalAccounts })}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t('payments.pages.paymentAccounts.sectionDescription')}</p>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <CreditCardListSection
          cards={cards}
          isLoading={isLoadingCards}
          onAddCard={() => openModal('credit-card')}
          onSetDefault={setDefault.submit}
          settingDefaultId={setDefault.pendingId}
          onEdit={(id) => setEditingCard(cards.find((card) => card.id === id) ?? null)}
          onRemove={setCardToRemove}
          paymentProfileIdsWithUpcomingPayment={paymentProfileIdsWithUpcomingPayment}
        />

        <div className="border-t pt-8">
          <BankAccountListSection
            accounts={bankAccounts}
            isLoading={isLoadingBanks}
            onAddAccount={() => openModal('bank-account')}
            onSetDefault={setDefault.submit}
            settingDefaultId={setDefault.pendingId}
            onEdit={(id) => setEditingBankAccount(bankAccounts.find((account) => account.id === id) ?? null)}
            onRemove={setAccountToRemove}
            paymentProfileIdsWithUpcomingPayment={paymentProfileIdsWithUpcomingPayment}
          />
        </div>
      </div>

      <AddPaymentAccountModal
        open={modalState.open}
        onOpenChange={(open) => setModalState((state) => ({ ...state, open }))}
        defaultTab={modalState.tab}
        onSuccess={handleSuccess}
      />

      <EditCreditCardModal card={editingCard} onClose={() => setEditingCard(null)} onSuccess={handleEditSuccess} />

      <EditBankAccountModal
        account={editingBankAccount}
        onClose={() => setEditingBankAccount(null)}
        onSuccess={handleEditBankAccountSuccess}
      />

      <RemoveCardDialog card={cardToRemove} onClose={() => setCardToRemove(null)} onSuccess={handleRemoveSuccess} />

      <RemoveBankAccountDialog
        account={accountToRemove}
        onClose={() => setAccountToRemove(null)}
        onSuccess={handleRemoveBankAccountSuccess}
      />
    </section>
  );
}
